import Vue from 'vue';
import {mapGetters} from 'vuex';
import Component, {mixins} from 'vue-class-component';
import {Prop, ProvideReactive} from 'vue-property-decorator';
import {DynamicElement, ElementConfig, Event, Order, OrderItem, Product, VisitorSurvey, VisitorSurveyQuestion} from '../../../Models';
import {CartDTO, VisitorSurveyResultDTO} from '../../../DTOs';
import {DynamicElementService} from '../../../Services';
import VisitorSurveyQuestionComponent from '../VisitorSurveyQuestionComponent/VisitorSurveyQuestionComponent.vue';
import {LanguageEnum} from '../../../Enums';
import {VuexObjectMixin} from '../../../Mixins';
import {Cart} from '../../Cart/Cart';

@Component({
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      currentOrder: 'getCurrentOrder',
    }),
  },
  components: {VisitorSurveyQuestionComponent},
})

export default class VisitorSurveyComponent extends Vue {
  @Prop({type: Event, required: true}) readonly event: Event | undefined;
  @Prop({type: VisitorSurveyResultDTO, required: false}) readonly initial: VisitorSurveyResultDTO | null | undefined;
  @Prop({type: String, required: true}) readonly inventoryUrl: string | undefined;
  @Prop({type: Array, required: true}) readonly orderItems: Array<OrderItem> | Array<Product> | undefined;
  @Prop({type: Boolean, required: false}) validateSurvey: boolean = false;

  @ProvideReactive() showErrors: boolean = false;

  // from computed mapGetters
  private userLanguage!: string;
  private currentOrder!: Order | null;

  private isRequesting: boolean = true;

  private visitorSurveyInstance!: VisitorSurvey;
  private visitorSurveyResultDTO: VisitorSurveyResultDTO = new VisitorSurveyResultDTO();
  private elements: Array<DynamicElement> = [];

  private dynamicElementService!: DynamicElementService;

  private surveyIsOptional: boolean = false;

  created() {
    this.dynamicElementService = new DynamicElementService(this.inventoryUrl);
    if (this.event && this.event.visitorSurvey) {
      this.visitorSurveyInstance = new VisitorSurvey(this.event.visitorSurvey.questions);
    }
    if (this.event) {
      this.visitorSurveyResultDTO.eventId = this.event.id;
    }

    if (this.currentOrder !== null) {
      this.visitorSurveyResultDTO.orderId = this.currentOrder.id;
    }

    if (this.initial) {
      this.visitorSurveyResultDTO = this.visitorSurveyResultDTO.fromData(this.initial);
    }
  }

  async mounted() {
    this.isRequesting = true;
    await this.fetchElements();
    this.translateElements();
    this.enrichElements();
    this.enrichSurvey();
    if (!this.showSurvey) {
      this.$emit('hideSurvey');
    }
    this.setSurveyIsOptional();
    if (this.surveyIsOptional) {
      this.setRequiredToFalseForAllQuestions();
    }
    this.prepareResults();
    this.isRequesting = false;
  }

  async fetchElements() {
    this.elements = [];
    const elementIds = this.extractElementIds();
    if (elementIds.length > 0) {
      this.elements = await this.dynamicElementService.list('', {id: JSON.stringify(elementIds)});
    }
  }

  validate(showErrors = false) {
    this.showErrors = showErrors;
    let valid = true;
    Object.keys(this.visitorSurveyResultDTO.results).forEach(qId => {
      valid = valid && this.visitorSurveyResultDTO.results[qId].valid;
    });
    return valid;
  }

  extractElementIds(): Array<number> {
    const ids: Array<number> = [];
    if (this.visitorSurveyInstance) {
      this.visitorSurveyInstance.questions.forEach(vsq => {
        ids.push(vsq.questionId);
        vsq.dependantQuestions.forEach(dq => {
          if (dq.questionId !== -1) {
            ids.push(dq.questionId);
          }
        });
      });
    }
    return ids;
  }

  enrichElements(): void {
    this.elements.forEach(e => {
      if (this.event && (e.eventId === this.event.id || !e.eventId)) {
        e.event = this.event;
      }
    });
  }

  enrichSurvey(): void {
    if (this.visitorSurveyInstance) {
      this.visitorSurveyInstance.questions.forEach(q => {
        const element = this.getElementById(q.questionId);
        if (element) {
          q.element = element;
        }
        q.dependantQuestions.forEach(dq => {
          const dependantElement = this.getElementById(dq.questionId);
          if (dependantElement) {
            dq.element = dependantElement;
          }
        });
      });
    }
  }

  getElementById(id: number): DynamicElement | undefined {
    // "find" is not unresolved, don't believe everything PhpStorm tells you
    return this.elements.find(e => e.id === id);
  }

  prepareResults() {
    if (!this.initial) {
      this.visitorSurveyInstance.questions.forEach(q => {
        this.visitorSurveyResultDTO.results[this.getQuestionResultKey(q.questionId)] = {};
      });
    }
  }

  updateQuestionData(data) {
    this.visitorSurveyResultDTO.results[this.getQuestionResultKey(data.value.id)] = data;
    this.$emit('update', this.visitorSurveyResultDTO);
  }

  translateElements() {
    const elements = [...this.elements];
    this.elements = [];
    // perform translation
    elements.forEach(e => {
      if (this.userLanguage && this.userLanguage !== LanguageEnum.DEFAULT && e.config.translations) {
        const originalLanguageIsRequired = e.config.isRequired;
        e.config = Object.assign(e.config, new ElementConfig(e.config.translations[this.userLanguage]));
        e.config.isRequired = originalLanguageIsRequired;
      }
      this.elements.push(e);
    });
  }

  getInitialQuestionData(question: VisitorSurveyQuestion) {
    const key = this.getQuestionResultKey(question.questionId);
    if (this.visitorSurveyResultDTO.results
      && this.visitorSurveyResultDTO.results[key]
      && Object.keys(this.visitorSurveyResultDTO.results[key]).length > 0) {
      return this.visitorSurveyResultDTO.results[key];
    }
    return null;
  }

  getQuestionResultKey(id: number | string) {
    return `question_${id}`;
  }

  get showSurvey(): boolean {
    if (this.orderItems) {
      for (const orderItem of this.orderItems) {
        if (orderItem.isRegistrationRequired || orderItem.isRegistrationOptional) {
          return true;
        }
      }
    }
    return false;
  }

  setRequiredToFalseForAllQuestions() {
    this.visitorSurveyInstance.questions.forEach(q => {
      q.element.config.isRequired = false;
    });
  }

  setSurveyIsOptional() {
    if (this.orderItems) {
      this.orderItems.forEach((orderItem) => {
        if (orderItem.isRegistrationRequired) {
          this.surveyIsOptional = false;
          return;
        } else if (orderItem.isRegistrationOptional) {
          this.surveyIsOptional = true;
        }
      });
    }
  }
}
